import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ExecuteCommandInput,
  FocusBucket,
  PortfolioView,
} from "@ceo-cmd/shared";
import {
  executeTodayItem,
  getTodayQueue,
  retryTodayItemEffects,
  undoTodayItem,
} from "@/api/today";

export function useTodayQueue(
  bucket: FocusBucket,
  view: PortfolioView = "group",
  companyId?: string,
) {
  return useQuery({
    queryKey: ["today", bucket, view, companyId ?? null],
    queryFn: () => getTodayQueue({ bucket, view, companyId }),
  });
}

export function useTodayExecute(
  bucket: FocusBucket,
  view: PortfolioView = "group",
  companyId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string;
      body?: ExecuteCommandInput;
    }) => executeTodayItem(itemId, body ?? {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["today"] });
      void queryClient.invalidateQueries({
        queryKey: ["today", bucket, view, companyId ?? null],
      });
    },
  });
}

export function useTodayRetryEffects(
  bucket: FocusBucket,
  view: PortfolioView = "group",
  companyId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      dispositionEventId,
    }: {
      itemId: string;
      dispositionEventId: string;
    }) => retryTodayItemEffects(itemId, dispositionEventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["today"] });
      void queryClient.invalidateQueries({
        queryKey: ["today", bucket, view, companyId ?? null],
      });
    },
  });
}

export function useTodayUndo(
  bucket: FocusBucket,
  view: PortfolioView = "group",
  companyId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => undoTodayItem(itemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["today"] });
      void queryClient.invalidateQueries({
        queryKey: ["today", bucket, view, companyId ?? null],
      });
    },
  });
}
